import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import clsx from "clsx";
import type { Tag } from "@poi-planner/shared";
import { themeColors } from "../theme/colors";
import { Button } from "./Button";
import { TagIcon } from "./TagIcon";

// Mini map pin preview matching MapView marker appearance
function MapPinPreview({ tag, size = 18 }: { tag: Tag; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: tag.color || themeColors.primary,
        border: "2px solid white",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
      }}
    >
      <TagIcon icon={tag.icon} size={size * 0.55} whiteTint />
    </div>
  );
}

const NO_HIDDEN_TAGS: string[] = [];

export interface TagPrimaryOptions {
  primaryTagId: string | null;
  onPrimaryChange: (tagId: string | null) => void;
  /** Opens the tag editor for the primary tag. */
  onEditTag: (tag: Tag) => void;
}

/**
 * Chip-style tag picker. Nothing is suggested until the user types; matches
 * come from the library (optionally ranked by the caller) and Enter creates a
 * tag that doesn't exist yet. With `primary`, a chip can be picked as the
 * place's map icon.
 */
export function TagChipInput({
  inputId,
  tags,
  allTags,
  onChange,
  onCreateTag,
  primary,
  rank,
  hiddenTagNames = NO_HIDDEN_TAGS,
  disabled = false,
}: {
  inputId: string;
  tags: string[];
  allTags: Tag[];
  onChange: (tags: string[]) => void;
  onCreateTag: (name: string) => Promise<string>;
  primary?: TagPrimaryOptions;
  /** Orders matches, e.g. tags most used on this trip's notes first. */
  rank?: (tags: Tag[]) => Tag[];
  /** Tags managed elsewhere in the editor (e.g. "accommodation"). */
  hiddenTagNames?: string[];
  disabled?: boolean;
}) {
  const [input, setInput] = useState("");
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const primaryTagId = primary?.primaryTagId ?? null;
  const hidden = useMemo(
    () => new Set(hiddenTagNames.map((name) => name.toLowerCase())),
    [hiddenTagNames],
  );

  const suggestions = useMemo(() => {
    const query = input.trim().toLowerCase();
    if (!query) return [];
    const matches = allTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(query) &&
        !tags.includes(tag.id) &&
        !hidden.has(tag.name.toLowerCase()),
    );
    return rank ? rank(matches) : matches;
  }, [input, allTags, tags, hidden, rank]);

  const canCreate =
    !!input.trim() &&
    !allTags.some(
      (tag) => tag.name.toLowerCase() === input.trim().toLowerCase(),
    );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsAutocompleteOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const reset = () => {
    setInput("");
    setIsAutocompleteOpen(false);
    setFocusedIndex(-1);
  };

  const addTag = async (tagName?: string) => {
    const trimmed = (tagName || input).trim().toLowerCase();
    if (trimmed) {
      const existingTag = allTags.find((t) => t.name.toLowerCase() === trimmed);
      const tagId = existingTag?.id ?? (await onCreateTag(trimmed));
      if (!tags.includes(tagId)) onChange([...tags, tagId]);
    }
    reset();
  };

  const selectSuggestion = (tag: Tag) => {
    if (!tags.includes(tag.id)) onChange([...tags, tag.id]);
    reset();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
        selectSuggestion(suggestions[focusedIndex]);
      } else {
        void addTag();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setIsAutocompleteOpen(true);
        setFocusedIndex((prev) => (prev + 1) % suggestions.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (suggestions.length > 0) {
        setIsAutocompleteOpen(true);
        setFocusedIndex((prev) =>
          prev <= 0 ? suggestions.length - 1 : prev - 1,
        );
      }
    } else if (e.key === "Escape" && isAutocompleteOpen && input.trim()) {
      // Close the list without closing the surrounding dialog.
      e.preventDefault();
      e.stopPropagation();
      setIsAutocompleteOpen(false);
      setFocusedIndex(-1);
    }
  };

  const displayTags = tags.filter((tagId) => {
    const tag = allTags.find((candidate) => candidate.id === tagId);
    return !tag || !hidden.has(tag.name.toLowerCase());
  });

  const showList = isAutocompleteOpen && (suggestions.length > 0 || canCreate);

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={input}
          disabled={disabled}
          onChange={(e) => {
            setInput(e.target.value);
            setIsAutocompleteOpen(true);
            setFocusedIndex(-1);
          }}
          onFocus={() => input.trim() && setIsAutocompleteOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Type to find or create a tag"
          autoComplete="off"
          className="input-base w-full"
        />

        {showList && (
          <div className="custom-scrollbar absolute top-full left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-xl border border-border bg-surface p-1.5 shadow-lg">
            {suggestions.map((tag, index) => (
              <Button
                key={tag.id}
                variant="unstyled"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectSuggestion(tag)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={clsx(
                  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-primary",
                  focusedIndex === index && "bg-subtle",
                )}
              >
                <MapPinPreview tag={tag} size={18} />
                {tag.name}
              </Button>
            ))}
            {canCreate && (
              <Button
                variant="unstyled"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void addTag()}
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm font-semibold text-info hover:bg-subtle"
              >
                <span
                  className="material-symbols-outlined text-base"
                  aria-hidden
                >
                  add
                </span>
                Create “{input.trim().toLowerCase()}”
              </Button>
            )}
          </div>
        )}
      </div>

      {displayTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayTags.map((tagId) => {
            const tagEntity = allTags.find((tag) => tag.id === tagId);
            const isPrimary = !!primary && primaryTagId === tagId;
            const canBePrimary = !!primary && !!tagEntity;

            return (
              <span
                key={tagId}
                className={clsx(
                  "inline-flex items-center gap-1 rounded-md border-2 py-1 pl-2 pr-1 text-xs font-semibold transition-all",
                  isPrimary
                    ? "border-primary bg-surface text-primary ring-2 ring-primary/20 shadow-md"
                    : "border-transparent bg-subtle text-secondary",
                )}
              >
                {canBePrimary ? (
                  <Button
                    variant="unstyled"
                    disabled={disabled}
                    aria-pressed={isPrimary}
                    onClick={() =>
                      primary.onPrimaryChange(isPrimary ? null : tagId)
                    }
                    className="flex cursor-pointer items-center gap-1.5 bg-transparent p-0 font-semibold text-secondary hover:opacity-80"
                    title={
                      isPrimary
                        ? "Click to remove map icon"
                        : "Click to use as map icon"
                    }
                  >
                    <MapPinPreview tag={tagEntity} size={20} />
                    {tagEntity.name}
                    {isPrimary && (
                      <span className="material-symbols-outlined text-xs text-primary">
                        location_on
                      </span>
                    )}
                  </Button>
                ) : (
                  <span className="flex items-center gap-1.5">
                    {tagEntity && <MapPinPreview tag={tagEntity} size={20} />}
                    {tagEntity?.name ?? "Unknown tag"}
                  </span>
                )}

                {!disabled && (
                  <Button
                    variant="unstyled"
                    onClick={() =>
                      isPrimary && tagEntity
                        ? primary.onEditTag(tagEntity)
                        : onChange(tags.filter((id) => id !== tagId))
                    }
                    aria-label={
                      isPrimary
                        ? `Edit ${tagEntity?.name ?? "tag"}`
                        : `Remove ${tagEntity?.name ?? "tag"}`
                    }
                    title={isPrimary ? "Edit tag" : "Remove tag"}
                    className={clsx(
                      "flex cursor-pointer items-center rounded bg-transparent p-0.5 transition-colors",
                      isPrimary
                        ? "text-primary/70 hover:bg-primary/10 hover:text-primary"
                        : "text-tertiary hover:bg-border hover:text-primary",
                    )}
                  >
                    <span className="material-symbols-outlined text-sm">
                      {isPrimary ? "edit" : "close"}
                    </span>
                  </Button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {primary && !primaryTagId && displayTags.length > 0 && (
        <p className="m-0 text-xs text-tertiary">
          Click a tag to use its icon on the map
        </p>
      )}
    </div>
  );
}
